import React from "react";
import Logo from "../../img/logo.svg";
import Button from "../Button";
import LocalizedLink from "../LocalizedLink";
import { CURRENT, LOCATIONS } from "../../config/cities";
import {
  CITIES_COUNT_DISPLAY,
  DOCTORS_COUNT,
  EXAMS_DONE,
  REMOVALS_DONE,
} from "../../config/trust";
import { GTM_LEAD_UMOW_SIE } from "../../config/gtm_classes";
import { useCountUp } from "../stats/useCountUp";

import "../../styles/article_conversion.scss";

// Every booking CTA on a content page goes to the city chooser on the home
// page, not to /kontakt — picking a city is the first real step of the funnel
// and the contact page made the reader take a detour to find it.
const CITY_CHOOSER = "/#kontakt";

// Współdzielony z SidebarPromo: ta sama obietnica na telefonie i na desktopie.
export const SIDEBAR_HEADLINE = "Nie zgaduj ze zdjęć — zbadaj swoje znamiona";

const STATS = [
  { value: EXAMS_DONE, label: "wideodermatoskopii" },
  { value: REMOVALS_DONE, label: "usuniętych zmian" },
  { value: DOCTORS_COUNT, label: "lekarzy w zespole" },
  { value: CITIES_COUNT_DISPLAY, label: "miast w Polsce" },
];

const COPY = {
  stats: {
    overline: "Centrum Badania Znamion",
    // Ten sam nagłówek co plansza w kolumnie bocznej — i to nie jest przeoczenie.
    // Karta jest wyłącznie mobilna, plansza wyłącznie desktopowa, więc nigdy nie
    // stoją obok siebie; jeden komunikat na obu szerokościach jest spójniejszy
    // niż dwa różne.
    //
    // Wcześniej stało tu „Zbadaliśmy znamiona podczas 68 000+ wizyt" — poprawne
    // po korekcie z „pacjentów", ale powtarzało liczbę, która i tak jest 30px
    // niżej w siatce. Nagłówek ma dawać powód, nie drugi raz tę samą liczbę.
    headline: SIDEBAR_HEADLINE,
    cta: "Umów badanie znamion",
  },
  closing: {
    // Nie „Twoje Znamiona" — to dosłownie powtórzenie napisu z logo stojącego
    // 12px obok. Nadtytuł ma dopowiadać, czym jesteśmy, a nie drugi raz mówić,
    // jak się nazywamy.
    overline: "Centrum Badania Znamion",
    headline: "Zbadaj znamiona w swoim mieście",
    cta: "Umów badanie znamion",
  },
};

/**
 * Blocks 2 and 4 — the same card in two roles.
 *
 * `stats` closes the first section and is the ONLY place carrying the full set
 * of numbers. `closing` ends the article and deliberately drops them: they were
 * already made, and repeating them here would read as a second pitch instead of
 * a next step. It offers the city list instead, which is what someone who read
 * to the end actually needs.
 *
 * 🔴 No heading tags inside. The side table of contents is authored in
 * frontmatter rather than parsed from the DOM, so an <h2> here would not break
 * it today — but it would put a marketing line into the document outline that
 * every future heading-based tool (TOC generation, SEO audits, screen-reader
 * navigation) reads as part of the article.
 */
const ArticleClinicCard = ({ variant = "stats" }: { variant?: "stats" | "closing" }) => {
  const copy = COPY[variant] || COPY.stats;
  const cities = LOCATIONS.filter((location) => location.type === CURRENT);
  const { ref, values: counted } = useCountUp(STATS.map((stat) => stat.value));

  return (
    <div
      className={`ac_card ac_card--${variant}`}
      data-article-card={variant}
      data-cta-surface={variant === "closing" ? "article_closing" : "article_stats_card"}
    >
      <div className="ac_card_head">
        <img className="ac_card_logo" src={Logo} alt="" aria-hidden="true" />
        <p className="ac_card_overline">{copy.overline}</p>
      </div>

      <p className="ac_card_headline">{copy.headline}</p>

      {variant === "stats" && (
        <div className="ac_card_stats" ref={ref}>
          {STATS.map((stat, index) => (
            <div className="ac_card_stat" key={stat.label}>
              <span className="ac_card_stat_value">{counted[index] ?? stat.value}</span>
              <span className="ac_card_stat_label">{stat.label}</span>
            </div>
          ))}
        </div>
      )}

      {variant === "closing" && (
        <div className="ac_card_cities">
          {cities.map((location) => (
            <LocalizedLink key={location.city} className="ac_card_city" to={`/${location.link}`}>
              {location.city}
            </LocalizedLink>
          ))}
        </div>
      )}

      <LocalizedLink className={`ac_card_cta ${GTM_LEAD_UMOW_SIE}`} to={CITY_CHOOSER}>
        <Button size="L">
          <span className={GTM_LEAD_UMOW_SIE}>{copy.cta}</span>
        </Button>
      </LocalizedLink>
    </div>
  );
};

export default ArticleClinicCard;
